/**
 * @file Frontend utilities — a3RawMaterialQualityField.ts.
 */

import type { FormDataResponse, FormField, FormSubSection } from "../interfaces/formInterfaces";
import { fetchRawMaterialQualityAttributes } from "../services/rawMaterialQualityAttributesPublicService";

/** A3 Manufacturing Plant Details — Material Quality field (per-material catalog attributes). */
export function isA3PlantMaterialQualityField(
  f: FormField,
  sectionCode?: string | null
): boolean {
  if (sectionCode !== "A3_MANUFACTURING_PLANT_DETAILS") return false;
  const compact = (f.name || "").toLowerCase().replace(/_/g, "");
  if (compact.startsWith("materialquality")) return true;
  const lab = (f.label || "").toLowerCase().replace(/\*/g, "").trim();
  if (lab === "material quality") return true;
  if (/^material quality\b/.test(lab)) return true;
  return false;
}

/**
 * @deprecated Use isA3PlantMaterialQualityField — kept for existing imports.
 */
export function isLegacyA3MaterialQualityCmsField(
  f: FormField,
  sectionCode?: string | null
): boolean {
  return isA3PlantMaterialQualityField(f, sectionCode);
}

/**
 * True when RAW_MATERIAL_QUALITY (or plant Material Quality) should show per-attribute numeric inputs.
 */
export function rawMaterialQualityUsesCatalogAttributes(
  f: FormField,
  sectionCode?: string | null
): boolean {
  if (isA3PlantMaterialQualityField(f, sectionCode)) return true;
  if (f.type !== "RAW_MATERIAL_QUALITY") return false;
  const inlineCount =
    (f.dropdown?.options?.length ?? 0) + (f.dropdownValues?.length ?? 0);
  const did = f.dropdownId != null ? Number(f.dropdownId) : NaN;
  const hasLink = Number.isFinite(did) && did > 0;
  return inlineCount === 0 && !hasLink;
}

/**
 * Value key for one RAW_MATERIAL_QUALITY sub-input (stable per attribute id).
 */
export function a3RmqValueKey(
  fieldName: string,
  attributeId: number,
  sectionCode: string,
  rowIndex = 0
): string {
  return `${fieldName}_rmq_${attributeId}_${sectionCode}_row${rowIndex}`;
}

/** Parse saved form keys into attribute/value pairs for API payload. */
export function collectA3MaterialQualityValuesForSave(
  values: Record<string, string>,
  fieldName: string,
  sectionCode: string,
  rowIndex = 0
): Array<{ attributeId: number; value: number }> {
  const prefix = `${fieldName}_rmq_`;
  const suffix = `_${sectionCode}_row${rowIndex}`;
  const results: Array<{ attributeId: number; value: number }> = [];

  for (const [key, raw] of Object.entries(values)) {
    if (!key.startsWith(prefix) || !key.endsWith(suffix)) continue;
    const middle = key.slice(prefix.length, key.length - suffix.length);
    const attributeId = Number(middle);
    if (!Number.isFinite(attributeId) || attributeId <= 0) continue;
    const trimmed = String(raw ?? "").trim();
    if (trimmed === "") continue;
    const value = Number(trimmed);
    if (!Number.isFinite(value)) continue;
    results.push({ attributeId, value });
  }

  return results;
}

function hasValue(raw: string | undefined): boolean {
  return String(raw ?? "").trim() !== "";
}

/** Validate mandatory per-attribute inputs for plant Material Quality. */
export function validateA3PlantMaterialQualityFields(params: {
  sections: FormSubSection[];
  values: Record<string, string>;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  const { sections, values } = params;

  for (const sec of sections) {
    const code = sec.code ?? "";
    if (code !== "A3_MANUFACTURING_PLANT_DETAILS") continue;

    for (const field of sec.fields) {
      if (!isA3PlantMaterialQualityField(field, code)) continue;
      if (!field.isEnabled || !field.isMandatory) continue;

      const attrs = field.rawMaterialQualityAttributes ?? [];
      if (attrs.length > 0) {
        for (const attr of attrs) {
          const id = Number(attr.id);
          if (!Number.isFinite(id) || id <= 0) continue;
          const key = a3RmqValueKey(field.name, id, code, 0);
          if (!hasValue(values[key])) {
            errors[key] = `${attr.label} is required.`;
          }
        }
        continue;
      }

      const collected = collectA3MaterialQualityValuesForSave(
        values,
        field.name,
        code,
        0
      );
      if (collected.length === 0) {
        const parentKey = `${field.name}_${code}_row0`;
        errors[parentKey] = `${field.label ?? "Material Quality"} is required.`;
      }
    }
  }

  return errors;
}

/**
 * Ensures `values` keys exist for each RAW_MATERIAL_QUALITY attribute.
 * Uses inline `rawMaterialQualityAttributes` when present; otherwise fetches catalog when `finishedMaterialId` is set.
 * Does not overwrite existing keys (e.g. from submission merge).
 * @param sectionRowCounts — e.g. `{ A3_PROCESS_DETAILS: 3 }` hydrates rows 0..2 for that section; others default to row 0 only.
 */
export async function hydrateA3RawMaterialQualityFields(
  init: Record<string, string>,
  data: FormDataResponse,
  finishedMaterialId?: number | null,
  sectionRowCounts?: Partial<Record<string, number>>
): Promise<void> {
  const mid =
    finishedMaterialId != null &&
    Number.isFinite(finishedMaterialId) &&
    finishedMaterialId > 0
      ? Math.floor(Number(finishedMaterialId))
      : null;

  for (const sec of data.formSubSections ?? []) {
    const sc = sec.code ?? "";
    const rows = Math.max(
      1,
      sectionRowCounts?.[sc] != null
        ? Math.floor(Number(sectionRowCounts[sc]))
        : 1
    );
    for (const f of sec.fields) {
      const usesCatalog =
        rawMaterialQualityUsesCatalogAttributes(f, sc) ||
        isA3PlantMaterialQualityField(f, sc);
      if (!usesCatalog) continue;
      if (f.type !== "RAW_MATERIAL_QUALITY" && !isA3PlantMaterialQualityField(f, sc)) {
        continue;
      }

      let attrs = f.rawMaterialQualityAttributes;
      if ((!attrs || attrs.length === 0) && mid != null) {
        attrs = await fetchRawMaterialQualityAttributes(mid);
      }
      const list = [...(attrs ?? [])].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );

      for (let ri = 0; ri < rows; ri++) {
        const baseKey = `${f.name}_${sc}_row${ri}`;
        if (init[baseKey] === undefined) init[baseKey] = "";

        for (const attr of list) {
          const id = Number(attr.id);
          if (!Number.isFinite(id) || id <= 0) continue;
          const sub = a3RmqValueKey(f.name, id, sc, ri);
          if (init[sub] === undefined) init[sub] = "";
        }
      }
    }
  }
}

/** Apply API `materialQualityValues` rows to form state keys. */
export function applyA3PlantMaterialQualityValuesToForm(
  nextValues: Record<string, string>,
  params: {
    fieldName: string;
    sectionCode: string;
    rowIndex: number;
    materialQualityValues: unknown;
  }
): void {
  const { fieldName, sectionCode, rowIndex, materialQualityValues } = params;
  if (!Array.isArray(materialQualityValues)) return;

  for (const raw of materialQualityValues) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as Record<string, unknown>;
    const attribute = row.attribute as Record<string, unknown> | undefined;
    const attrId = Number(row.attributeId ?? row.attribute_id ?? attribute?.id);
    if (!Number.isFinite(attrId) || attrId <= 0) continue;
    const val = row.value;
    if (val === null || val === undefined || val === "") continue;
    const key = a3RmqValueKey(fieldName, attrId, sectionCode, rowIndex);
    nextValues[key] = String(val).trim();
  }
}
